import { ReactNode } from 'react'
import AvatarPet from '~components/ui/avatar/avatar-pet'
import Media from '~components/ui/media'
import Pet from '~models/pet'
import { cn } from '~utils/cn'

type Props = Readonly<{
  children: ReactNode
  className?: string
  onClick?: () => void
}>

type ConsultationCardMediaProps = Readonly<{
  url: string
  alt: string
}>

const ConsultationCardMain = ({ children, className, onClick }: Props) => {
  return (
    <div
      role="presentation"
      className={cn('flex cursor-pointer flex-col flex-gap-y-2', className)}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

const ConsultationCardTitle = ({ children, className }: Props) => {
  return (
    <h4
      className={cn(
        'mb-2 line-clamp-2 break-words text-black text-title-2 dark:text-white lg:text-headline-w-4',
        className
      )}
    >
      {children}
    </h4>
  )
}

const ConsultationCardContent = ({ children, className }: Props) => {
  return (
    <p
      className={cn(
        'line-clamp-3 break-words text-gray-900 text-body-3 dark:text-gray-500',
        className
      )}
    >
      {children}
    </p>
  )
}

const ConsultationCardLabel = ({ children, className }: Props) => {
  return (
    <div
      className={cn(
        'w-fit rounded-lg bg-white px-2 py-1 text-gray-900 text-label-3 dark:bg-gray-800 dark:text-gray-500',
        className
      )}
    >
      {children}
    </div>
  )
}

const ConsultationCardMedia = ({ url, alt }: ConsultationCardMediaProps) => {
  return <Media url={url} alt={alt} size="64" />
}

const ConsultationCardPet = ({ pet }: Readonly<{ pet: Pet.Level1 }>) => {
  return (
    <div className="flex items-center flex-gap-x-2">
      <AvatarPet pet={pet} size="24" />
      <div className="text-gray-900 text-body-3">{pet.kind.depth1 || 'Unknown'}</div>
      {pet.kind.depth2 && <div className="text-gray-500 text-body-3">{pet.kind.depth2}</div>}
    </div>
  )
}

const ConsultationCard = Object.assign(ConsultationCardMain, {
  Title: ConsultationCardTitle,
  Content: ConsultationCardContent,
  Label: ConsultationCardLabel,
  Media: ConsultationCardMedia,
  Pet: ConsultationCardPet,
})

export default ConsultationCard
